import React from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import {
  Panel,
  PanelBody,
  PanelHeader,
} from "../../components/panel/panel.jsx";
import { DatePicker, Tabs } from "antd";
import { getCurrentFortnightDayjs } from "../../models/Payroll.ts";
import {
  findActiveDeductions,
  findDeductionsByPartner,
} from "../../services/deductions.service.ts";
import { autocompletePartners } from "../../services/partner.service.ts";
import { getProfileImage } from "../../services/profileImageCache.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import NewDeduction from "../../components/deduction/NewDeduction.tsx";
import DeleteDeductionModal from "../../components/deduction/DeleteDeductionModal.tsx";
import EmptyImage from "../../components/EmptyImage.tsx";
import { useDispatch } from "react-redux";
import { setSelectedPartners } from "../../store/slices/payrollSlice.ts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";

const { RangePicker } = DatePicker;

const formatDisplayName = (raw: string | undefined | null): string => {
  if (raw == null || String(raw).trim() === "") return "";
  return String(raw)
    .trim()
    .split(/\s+/)
    .map((w) =>
      w.length ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w
    )
    .join(" ");
};

const formatDateLong = (date: string): string => {
  if (!date || date === "-" || date === "" || date === "Invalid Date")
    return "-";
  try {
    let dateObj: Date;
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [y, m, d] = date.split("-").map(Number);
      dateObj = new Date(y, m - 1, d);
    } else {
      dateObj = new Date(
        String(date).includes("T") ? date : `${date}T12:00:00`
      );
    }
    if (Number.isNaN(dateObj.getTime())) return "-";
    const months = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];
    return `${dateObj.getDate()} ${months[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
  } catch {
    return "-";
  }
};

const PartnerSideListRow = ({
  partner,
  disabled,
  onPick,
}: {
  partner: any;
  disabled: boolean;
  onPick: (p: any) => void;
}) => {
  const [img, setImg] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    getProfileImage(partner.id).then((url) => {
      if (!cancelled) setImg(url);
    });
    return () => {
      cancelled = true;
    };
  }, [partner.id]);

  const name = `${partner?.person?.firstName || ""} ${
    partner?.person?.lastName || ""
  }`.trim();
  const puesto =
    partner?.candidate?.vacancy?.position?.name || "Sin puesto";
  const sucursal =
    partner?.candidate?.branch?.name || "Sin sucursal";

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !disabled) {
          e.preventDefault();
          onPick(partner);
        }
      }}
      onClick={() => {
        if (!disabled) onPick(partner);
      }}
      style={{
        alignItems: "center",
        backgroundColor: disabled ? "#f8f9fa" : "#fff",
        borderBottom: "1px solid #eef2f7",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        gap: "0.75rem",
        opacity: disabled ? 0.65 : 1,
        padding: "0.65rem 0.75rem",
      }}
    >
      <div
        style={{
          alignItems: "center",
          backgroundColor: "#f3f4f6",
          borderRadius: "50%",
          display: "flex",
          flexShrink: 0,
          height: "44px",
          justifyContent: "center",
          overflow: "hidden",
          width: "44px",
        }}
      >
        {img ? (
          <img
            src={img}
            alt=""
            style={{ height: "100%", objectFit: "cover", width: "100%" }}
            onError={() => setImg(null)}
          />
        ) : (
          <i className="fa-regular fa-user" style={{ color: "#9ca3af" }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: "#101828",
            fontSize: "0.9rem",
            fontWeight: 700,
            lineHeight: 1.25,
          }}
        >
          {formatDisplayName(name) || "Sin nombre"}
          {disabled && (
            <span className="ms-2 text-muted" style={{ fontSize: "0.72rem" }}>
              <i className="fa fa-check" /> En lista
            </span>
          )}
        </div>
        <div
          style={{
            color: "#475467",
            fontSize: "0.78rem",
            fontWeight: 600,
            marginTop: "0.15rem",
          }}
        >
          {formatDisplayName(puesto)}
        </div>
        <div style={{ color: "#667085", fontSize: "0.74rem", marginTop: "0.1rem" }}>
          <i className="fa fa-building me-1" style={{ opacity: 0.7 }} />
          {formatDisplayName(sucursal)}
        </div>
      </div>
    </div>
  );
};

const DeduccionesPage = () => {
  const [dates, setDates] = React.useState(getCurrentFortnightDayjs());
  const [activeKey, setActiveKey] = React.useState("todos");
  const [loading, setLoading] = React.useState(false);
  const [baseAll, setBaseAll] = React.useState<any[]>([]);
  const [manualAll, setManualAll] = React.useState<any[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<(string | number)[]>([]);
  const [modal, setModal] = React.useState(false);
  const [editModal, setEditModal] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);
  const [selectedDeduction, setSelectedDeduction] = React.useState<any>(null);
  const [searchPartner, setSearchPartner] = React.useState("");
  const [filteredPartners, setFilteredPartners] = React.useState<any[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(true);
  const [sortBy, setSortBy] = React.useState<string>("");
  const [defaultSort, setDefaultSort] = React.useState<"asc" | "desc">("asc");
  const timeoutId = React.useRef<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedPartnerIds = useSelector(
    (state: RootState) => state.payroll.selectedPartners
  );
  useAppSettings();

  const formatMoney = (val: any) => {
    if (val === "-" || val === undefined || val === null) return "-";
    const num = Number(val);
    if (Number.isNaN(num)) return String(val);
    return num.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 2,
    });
  };

  const isUniqueFrequency = (frequency: any) => {
    if (!frequency) return false;
    const freqStr = String(frequency).toLowerCase();
    return freqStr === "único" || freqStr === "unico" || frequency === 4;
  };

  const getStartDate = (row: any) => {
    if (isUniqueFrequency(row.frequency)) {
      return row.applicationDate || "-";
    }
    return row.startDate || "-";
  };

  const getEndDate = (row: any) => {
    if (isUniqueFrequency(row.frequency)) {
      return "Fecha única";
    }
    return row.endDate || "-";
  };

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";

    setSortBy(sort);
    setDefaultSort(sortOrder);
  };

  const sortData = (data: any[]) => {
    if (!sortBy) return data;

    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case "colaborador":
          aValue = `${a?.partner?.person?.firstName || ""} ${
            a?.partner?.person?.lastName || ""
          }`.toLowerCase();
          bValue = `${b?.partner?.person?.firstName || ""} ${
            b?.partner?.person?.lastName || ""
          }`.toLowerCase();
          break;
        case "descripcion":
          aValue = (a.description || "").toLowerCase();
          bValue = (b.description || "").toLowerCase();
          break;
        case "nombre":
          aValue = (a.name || "").toLowerCase();
          bValue = (b.name || "").toLowerCase();
          break;
        case "monto":
          aValue = Number(a.amount) || 0;
          bValue = Number(b.amount) || 0;
          break;
        case "fechaInicio":
          aValue = new Date(a.startDate || "").getTime();
          bValue = new Date(b.startDate || "").getTime();
          break;
        case "fechaFin":
          aValue = new Date(a.endDate || "").getTime();
          bValue = new Date(b.endDate || "").getTime();
          break;
        case "estatus":
          aValue = (a.status || "").toLowerCase();
          bValue = (b.status || "").toLowerCase();
          break;
        case "creadoPor":
          aValue = a.createdBy ? `${a.createdBy.name} ${a.createdBy.lastName}`.toLowerCase() : "";
          bValue = b.createdBy ? `${b.createdBy.name} ${b.createdBy.lastName}`.toLowerCase() : "";
          break;
        case "creado":
          aValue = new Date(a.createdAt || "").getTime();
          bValue = new Date(b.createdAt || "").getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return defaultSort === "asc" ? -1 : 1;
      if (aValue > bValue) return defaultSort === "asc" ? 1 : -1;
      return 0;
    });
  };

  const parseDateValue = (value: any) => {
    if (!value || value === "-" || value === "Invalid Date") return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isInSelectedRange = (row: any) => {
    const rangeStart = dates.start.startOf("day").toDate();
    const rangeEnd = dates.end.endOf("day").toDate();

    const startDate = parseDateValue(row.startDate);
    const endDate = parseDateValue(row.endDate);
    const applicationDate = parseDateValue(row.applicationDate);
    const createdAt = parseDateValue(row.createdAt);

    if (isUniqueFrequency(row.frequency) && applicationDate) {
      return applicationDate >= rangeStart && applicationDate <= rangeEnd;
    }

    if (startDate && endDate) {
      return startDate <= rangeEnd && endDate >= rangeStart;
    }

    if (startDate && !endDate) {
      return startDate <= rangeEnd;
    }

    if (!startDate && !endDate && applicationDate) {
      return applicationDate >= rangeStart && applicationDate <= rangeEnd;
    }

    if (!startDate && !endDate && createdAt) {
      return createdAt >= rangeStart && createdAt <= rangeEnd;
    }

    return true;
  };

  const fetchDeductions = async () => {
    console.log("Fetching deductions...");
    setLoading(true);
    try {
      const start = dates.start.startOf("day").toISOString();
      const end = dates.end.endOf("day").toISOString();
      const resp = await findActiveDeductions({ start, end });
      console.log("Deductions fetched:", resp);
      setBaseAll((resp || []).filter(isInSelectedRange));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setManualAll([]);
    setSelectedIds([]);
    fetchDeductions();
  }, [dates.start, dates.end]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setSearchLoading(true);
        const data = await autocompletePartners("");
        if (!cancelled) {
          setFilteredPartners(data || []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleSelected = (id: string | number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev]
    );
  };

  const mergeUniqueById = (a: any[], b: any[]) => {
    const map = new Map();
    [...a, ...b].forEach((item) => {
      const key = item.id;
      if (!map.has(key)) map.set(key, item);
    });
    return Array.from(map.values());
  };

  const all = React.useMemo(
    () => mergeUniqueById(manualAll, baseAll),
    [manualAll, baseAll]
  );

  const sortedAll = React.useMemo(
    () => sortData(all),
    [all, sortBy, defaultSort]
  );

  const selectedRows = React.useMemo(() => {
    return sortedAll.filter((row) => selectedIds.includes(row.id));
  }, [sortedAll, selectedIds]);

  const partnersSidebarBrowse = React.useMemo(
    () => (filteredPartners || []).slice(0, 10),
    [filteredPartners]
  );

  const handleOpenModal = () => {
    const partnerIds = selectedRows
      .map((row) => {
        if (row.partner?.id) {
          return row.partner.id;
        }

        if (
          row.id &&
          typeof row.id === "string" &&
          row.id.startsWith("placeholder-")
        ) {
          const parts = row.id.split("-");
          return parseInt(parts[1]);
        }
        return null;
      })
      .filter((id) => id !== null) as number[];

    console.log("Selected rows:", selectedRows);
    console.log("Extracted partner IDs:", partnerIds);

    dispatch(setSelectedPartners(partnerIds));
    setModal(true);
  };

  const handleCloseModal = () => {
    setModal(false);
    dispatch(setSelectedPartners([]));
  };

  const handleEdit = (deduction: any) => {
    setSelectedDeduction(deduction);
    setEditModal(true);
  };

  const handleDelete = (deduction: any) => {
    setSelectedDeduction(deduction);
    setDeleteModal(true);
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setSelectedDeduction(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setSelectedDeduction(null);
  };

  const handleDeleteSuccess = () => {
    fetchDeductions();
    handleCloseDeleteModal();
  };

  const handleCreateSuccess = () => {
    fetchDeductions();

    setManualAll([]);
  };

  const handleRemoveFromList = (deduction: any) => {
    setManualAll((prev) => prev.filter((item) => item.id !== deduction.id));
    setSelectedIds((prev) => prev.filter((id) => id !== deduction.id));
  };

  const isPartnerAlreadyAdded = (partnerId: number) => {
    return (
      all.some((item) => {
        if (item.partner?.id === partnerId) return true;

        if (item.id === `placeholder-${partnerId}`) return true;
        return false;
      }) || selectedPartnerIds.includes(partnerId)
    );
  };

  const handlePartnerPick = async (p: any) => {
    if (isPartnerAlreadyAdded(p.id)) return;

    setSearchPartner("");

    try {
      setLoading(true);
      const deductions = await findDeductionsByPartner(p.id);
      const mappedAll = Array.isArray(deductions)
        ? deductions.map((ded: any) => ({
            ...ded,
            partner: {
              id: p.id,
              person: {
                firstName: p?.person?.firstName,
                lastName: p?.person?.lastName,
              },
            },
          }))
        : [];
      const mapped = mappedAll.filter(isInSelectedRange);

      if (mapped.length > 0) {
        setManualAll((prev) => [...mapped, ...prev]);
        setSelectedIds((prev) =>
          Array.from(
            new Set([
              ...mapped
                .map((ded: any) => ded.id)
                .filter(
                  (id: string | number | null | undefined) =>
                    id !== null && id !== undefined
                ),
              ...prev,
            ])
          )
        );
      } else {
        const placeholderId = `placeholder-${p.id}-${Date.now()}`;
        const placeholder = {
          id: placeholderId,
          partner: {
            id: p.id,
            person: {
              firstName: p?.person?.firstName,
              lastName: p?.person?.lastName,
            },
          },
          description: "-",
          amount: "-",
          startDate: "-",
          endDate: "-",
          status: "-",
        };
        setManualAll((prev) => [placeholder, ...prev]);
        setSelectedIds((prev) =>
          Array.from(new Set([placeholderId, ...prev]))
        );
      }

      try {
        const data = await autocompletePartners("");
        setFilteredPartners(data || []);
      } catch {
        /* mantener lista actual */
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const surfacePadding = "1.1rem";
  const surfaceRadius = "20px";

  const cardStyle = {
    border: "1px solid #e4e7ec",
    borderRadius: surfaceRadius,
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.07)",
    background: "#fff",
    overflow: "hidden",
  };

  const sectionTitleStyle = {
    color: "#101828",
    display: "block",
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "0.2px",
    lineHeight: 1.35,
  };

  const helperTextStyle = {
    color: "#667085",
    fontSize: "0.82rem",
    marginTop: "0.35rem",
  };

  const listHeaderToolbarStyle = {
    alignItems: "flex-start",
    borderBottom: "1px solid #eef2f7",
    display: "flex",
    flexDirection: "row" as const,
    flexWrap: "wrap" as const,
    gap: "1rem",
    justifyContent: "space-between",
    padding: surfacePadding,
  };

  const actionButtonStyle = {
    borderRadius: "10px",
    fontWeight: 600,
    boxShadow: "0 4px 12px rgba(220, 53, 69, 0.25)",
    padding: "0.7rem 1.1rem",
  };

  const tableBoxStyle = {
    overflowY: "auto" as const,
    maxHeight: "500px",
    minHeight: "500px",
    backgroundColor: "#fafbfc",
    border: "1px solid #d0d5dd",
    borderRadius: "12px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
  };

  const stickyHeaderRowStyle = {
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#eef2f6",
    borderBottom: "1px solid #dce3ed",
    zIndex: 2,
  };

  const sortableThStyle = (column: string): React.CSSProperties => ({
    backgroundColor: "transparent",
    borderBottom:
      sortBy === column ? "2px solid #dc2626" : "2px solid transparent",
    boxShadow:
      sortBy === column ? "none" : "inset 0 -1px 0 rgba(148, 163, 184, 0.45)",
    color: sortBy === column ? "#0f172a" : "#64748b",
    cursor: "pointer",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    padding: "12px 14px",
    textTransform: "uppercase" as const,
    userSelect: "none" as const,
    whiteSpace: "nowrap" as const,
  });

  const staticThStyle: React.CSSProperties = {
    backgroundColor: "transparent",
    borderBottom: "2px solid transparent",
    boxShadow: "inset 0 -1px 0 rgba(148, 163, 184, 0.45)",
    color: "#64748b",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    padding: "12px 14px",
    textTransform: "uppercase" as const,
    whiteSpace: "nowrap" as const,
  };

  const checkboxThStyle: React.CSSProperties = {
    backgroundColor: "transparent",
    borderBottom: "2px solid transparent",
    boxShadow: "inset 0 -1px 0 rgba(148, 163, 184, 0.45)",
    padding: "10px 8px",
    width: 42,
  };

  const summaryPillStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.8rem",
    padding: surfacePadding,
    border: "1px solid #e4e7ec",
    borderRadius: surfaceRadius,
    background: "#fff",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.07)",
  };

  const summaryIconStyle = {
    alignItems: "center",
    borderRadius: "10px",
    display: "inline-flex",
    height: "34px",
    justifyContent: "center",
    width: "34px",
  };

  const pageShellStyle = {
    background:
      "linear-gradient(180deg, #fdf6f6 0%, #faf8f8 38%, #ffffff 100%)",
    borderRadius: "24px",
    padding: "1.5rem",
    //margin x 1rem margin y 1rem 
    marginLeft: "1rem",   
    marginRight: "1rem",
    marginTop: "1rem",
    marginBottom: "1rem",
  };

  const heroStyle = {
    alignItems: "center",
    background: "transparent",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "1rem",
    justifyContent: "space-between",
    marginBottom: "1.25rem",
    padding: "0 0 0.25rem",
  };

  const heroEyebrowStyle = {
    alignItems: "center",
    background: "transparent",
    border: "none",
    borderRadius: "999px",
    color: "#b42318",
    display: "inline-flex",
    fontSize: "0.75rem",
    fontWeight: 800,
    gap: "0.45rem",
    marginBottom: "0.75rem",
    padding: "0.15rem 0",
  };

  const heroTitleStyle = {
    color: "#991b1b",
    fontSize: "1.75rem",
    fontWeight: 850,
    letterSpacing: "-0.03em",
    margin: 0,
  };

  const twoColumnLayoutStyle = {
    alignItems: "stretch",
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "1.25rem",
    marginBottom: "1.25rem",
  };

  const sideColumnStyle = {
    display: "flex",
    flex: "0 0 calc(30% - 0.65rem)",
    flexDirection: "column" as const,
    gap: "1.25rem",
    minWidth: 280,
  };

  const contentColumnStyle = {
    flex: "1 1 calc(70% - 0.65rem)",
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
  };

  const rangeCardStyle = {
    ...summaryPillStyle,
    alignItems: "flex-start",
    flexDirection: "column" as const,
    minHeight: "auto",
  };

  const listCardStyle = {
    ...cardStyle,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column" as const,
    minHeight: 0,
    height: "100%",
    borderRadius: surfaceRadius,
    overflow: "hidden",
  };

  const summaryLabelStyle = {
    color: "#667085",
    display: "block",
    fontSize: "0.72rem",
    fontWeight: 600,
    lineHeight: 1,
    textTransform: "uppercase" as const,
  };

  const summaryValueStyle = {
    color: "#101828",
    display: "block",
    fontSize: "1rem",
    fontWeight: 800,
    lineHeight: 1.3,
  };

  const rowActionButtonStyle = {
    alignItems: "center",
    borderRadius: "999px",
    display: "inline-flex",
    height: "32px",
    justifyContent: "center",
    width: "32px",
  };

  const isPlaceholderRow = (row: any) =>
    row?.id?.toString().startsWith("placeholder-");

  const getDeductionRowStyle = (row: any, idx: number): React.CSSProperties => {
    if (isPlaceholderRow(row)) {
      return {
        backgroundColor: "#fff5f5",
        borderBottom: "1px solid #fecdd3",
        boxShadow: "inset 4px 0 0 #fb7185",
        color: "#0f172a",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
      };
    }
    return {
      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
      borderBottom: "1px solid #e8ecf1",
      color: "#0f172a",
      cursor: "pointer",
      transition: "background-color 0.15s ease",
    };
  };

  const renderPartnerCell = (row: any) => {
    const rawName = `${row?.partner?.person?.firstName || ""} ${
      row?.partner?.person?.lastName || ""
    }`.trim();
    const name = rawName ? formatDisplayName(rawName) : "";

    return (
      <div style={{ minWidth: 180 }}>
        <div style={{ color: "#101828", fontWeight: 700 }}>
          {name || "Sin colaborador"}
        </div>
        {isPlaceholderRow(row) && (
          <span
            style={{
              background: "#fef3f2",
              border: "1px solid #fecdca",
              borderRadius: "999px",
              color: "#b42318",
              display: "inline-flex",
              fontSize: "0.72rem",
              fontWeight: 700,
              marginTop: "0.3rem",
              padding: "0.25rem 0.55rem",
            }}
          >
            Por capturar
          </span>
        )}
      </div>
    );
  };

  const renderDescriptionCell = (row: any) => {
    const text =
      row.description != null &&
      row.description !== "" &&
      row.description !== "-"
        ? String(row.description)
        : "-";
    if (text === "-") {
      return (
        <span className="text-muted" style={{ fontWeight: 500 }}>
          -
        </span>
      );
    }
    const showTip = text.length > 72;
    const inner = (
      <div
        style={
          {
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            color: "#344054",
            display: "-webkit-box",
            fontWeight: 500,
            lineHeight: 1.35,
            maxWidth: 280,
            overflow: "hidden",
            wordBreak: "break-word",
            whiteSpace: "normal",
          } as React.CSSProperties
        }
      >
        {text}
      </div>
    );
    if (showTip) {
      return (
        <Tooltip title={text} arrow placement="top">
          <div
            style={{
              cursor: "default",
              display: "inline-block",
              maxWidth: 280,
            }}
          >
            {inner}
          </div>
        </Tooltip>
      );
    }
    return inner;
  };

  const renderAmountCell = (amount: any) => (
    <span
      style={{
        background: amount === "-" ? "#f8fafc" : "#fef3f2",
        border: amount === "-" ? "1px solid #e4e7ec" : "1px solid #fecdca",
        borderRadius: "999px",
        color: amount === "-" ? "#667085" : "#b42318",
        display: "inline-flex",
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        fontSize: "0.9rem",
        fontVariantNumeric: "tabular-nums",
        fontWeight: 700,
        justifyContent: "center",
        letterSpacing: "0.02em",
        minWidth: 96,
        padding: "0.42rem 0.75rem",
      }}
    >
      {formatMoney(amount)}
    </span>
  );

  const renderEndDateCell = (row: any) =>
    isUniqueFrequency(row.frequency) ? (
      <span
        style={{
          background: "#f2f4f7",
          border: "1px solid #e4e7ec",
          borderRadius: "999px",
          color: "#475467",
          display: "inline-flex",
          fontSize: "0.78rem",
          fontWeight: 700,
          padding: "0.35rem 0.6rem",
        }}
      >
        Fecha única
      </span>
    ) : (
      formatDateLong(getEndDate(row))
    );

  const renderRowActions = (row: any) =>
    !isPlaceholderRow(row) ? (
      <div className="d-flex align-items-center" style={{ gap: "0.4rem" }}>
        <button
          className="btn btn-sm btn-light"
          style={{
            ...rowActionButtonStyle,
            border: "1px solid #b9e6fe",
            color: "#026aa2",
          }}
          onClick={() => handleEdit(row)}
          title="Editar deducción"
        >
          <i className="fa fa-edit"></i>
        </button>
        <button
          className="btn btn-sm btn-light"
          style={{
            ...rowActionButtonStyle,
            border: "1px solid #fecdca",
            color: "#b42318",
          }}
          onClick={() => handleDelete(row)}
          title="Eliminar deducción"
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
    ) : (
      <button
        className="btn btn-sm btn-light"
        style={{
          ...rowActionButtonStyle,
          border: "1px solid #fed7aa",
          color: "#c2410c",
        }}
        onClick={() => handleRemoveFromList(row)}
        title="Quitar de la lista"
      >
        <i className="fa fa-times"></i>
      </button>
    );

  const renderEmptyState = (title: string, subtitle: string) => (
    <div className="d-flex align-items-center justify-content-center">
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ maxWidth: 420 }}
      >
        <EmptyImage className="mx-auto w-250px" />
        <h5
          className="text-secondary text-center fs-20px mt-3 mb-2"
          style={{ color: "#344054", fontWeight: 800 }}
        >
          {title}
        </h5>
        <p
          className="text-center mb-0"
          style={{ color: "#667085", fontSize: "0.92rem" }}
        >
          {subtitle}
        </p>
      </div>
    </div>
  );

  const renderPartnerSearchField = () => (
    <div style={{ position: "relative" as const, width: "100%", minWidth: 0 }}>
      <div className="input-group">
        <div
          className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
          style={{ zIndex: 10, background: "transparent" }}
        >
          {searchLoading ? (
            <i className="fa fa-spinner fa-spin" />
          ) : (
            <i className="fa fa-search opacity-5" />
          )}
        </div>
        <input
          type="text"
          className="form-control ps-35px"
          placeholder="Buscar colaborador..."
          style={{
            borderRadius: "10px",
            border: "1px solid #d0d5dd",
            boxShadow: "none",
          }}
          value={searchPartner}
          onChange={(e) => {
            const val = e.target.value;
            setSearchPartner(val);
            if (timeoutId.current) clearTimeout(timeoutId.current);
            timeoutId.current = setTimeout(async () => {
              try {
                setSearchLoading(true);
                const data = await autocompletePartners(val);
                setFilteredPartners(data || []);
              } finally {
                setSearchLoading(false);
              }
            }, 400);
          }}
        />
      </div>
    </div>
  );

  return (
    <Layout loading={loading}>
      <div style={pageShellStyle}>
        <div style={heroStyle}>
          <div>
            <div style={heroEyebrowStyle}>
              <i className="fa fa-minus-circle"></i>
              Control de nómina
            </div>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexWrap: "wrap" as const,
                gap: "0.65rem",
              }}
            >
              <button
                type="button"
                className="btn btn-dark border-0 d-inline-flex align-items-center justify-content-center p-0"
                onClick={() => navigate("/nomina")}
                title="Volver a nómina"
                aria-label="Volver a nómina"
                style={{
                  borderRadius: "16px",
                  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.18)",
                  color: "#ffffff",
                  flexShrink: 0,
                  height: "44px",
                  width: "44px",
                }}
              >
                <i className="fa fa-arrow-left" style={{ color: "#ffffff" }} aria-hidden />
              </button>
              <h2 style={heroTitleStyle}>Deducciones</h2>
            </div>
          </div>
          <button
            className="btn btn-light"
            onClick={() => navigate("/nomina/plantillas")}
            title="Gestionar mis plantillas"
            style={{
              border: "1px solid #fecdca",
              borderRadius: "14px",
              boxShadow: "0 10px 22px rgba(180, 35, 24, 0.12)",
              color: "#b42318",
              fontWeight: 800,
              padding: "0.75rem 1rem",
            }}
          >
            <i className="fa fa-bookmark me-2"></i>
            Mi Lista
          </button>
        </div>

        <div style={twoColumnLayoutStyle}>
          <div style={sideColumnStyle}>
          <div style={rangeCardStyle}>
            <div>
              <span style={summaryLabelStyle}>Periodo de consulta</span>
              <span style={summaryValueStyle}>Rango de fecha</span>
            </div>
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={["Fecha de inicio", "Fecha de fin"]}
              style={{ width: "100%" }}
              size="large"
              allowClear={false}
              defaultValue={[dates.start, dates.end]}
              onChange={(d) => {
                if (d && d[0] && d[1]) setDates({ start: d[0], end: d[1] });
              }}
            />
          </div>
          <div
            style={{
              ...summaryPillStyle,
              flexDirection: "column" as const,
              alignItems: "stretch",
              gap: 0,
              minHeight: "auto",
              padding: 0,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                borderBottom: "1px solid #eef2f7",
                padding: surfacePadding,
              }}
            >
              <span style={summaryLabelStyle}>Colaboradores</span>
              <div style={{ marginTop: "0.75rem" }}>{renderPartnerSearchField()}</div>
            </div>
            <div
              style={{
                maxHeight: "420px",
                overflowY: "auto" as const,
              }}
            >
              {partnersSidebarBrowse.length === 0 ? (
                <div
                  className="text-muted small"
                  style={{ padding: surfacePadding, minHeight: "2.5rem" }}
                >
                  {searchLoading ? "Cargando…" : "Sin coincidencias."}
                </div>
              ) : (
                partnersSidebarBrowse.map((p) => (
                  <PartnerSideListRow
                    key={p.id}
                    partner={p}
                    disabled={isPartnerAlreadyAdded(p.id)}
                    onPick={handlePartnerPick}
                  />
                ))
              )}
            </div>
          </div>
          </div>

          <div style={contentColumnStyle}>
        <div
          className="card"
          style={listCardStyle}
        >
          <Panel className="mb-0 border-0 bg-transparent shadow-none flex-fill d-flex flex-column">
            <PanelBody className="p-0 d-flex flex-column flex-fill">
              <div style={listHeaderToolbarStyle}>
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <span style={sectionTitleStyle}>Listado de deducciones</span>
                  <div style={helperTextStyle}>
                    Selecciona filas y usa &quot;Agregar deducciones&quot;. Los
                    colaboradores del panel izquierdo se agregan a la tabla al
                    tocarlos.
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <button
                    className="btn btn-danger btn-lg px-4"
                    style={{
                      ...actionButtonStyle,
                      boxShadow:
                        selectedRows.length === 0
                          ? "none"
                          : "0 4px 12px rgba(220, 53, 69, 0.25)",
                      cursor:
                        selectedRows.length === 0 ? "not-allowed" : "pointer",
                      opacity: selectedRows.length === 0 ? 0.62 : 1,
                    }}
                    onClick={handleOpenModal}
                    disabled={selectedRows.length === 0}
                    title={
                      selectedRows.length === 0
                        ? "Selecciona al menos un colaborador"
                        : ""
                    }
                  >
                    <i className="fa fa-minus me-2"></i>
                    Agregar deducciones
                  </button>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flex: "1 1 auto",
                  flexDirection: "column" as const,
                  minHeight: 0,
                  padding: `0 ${surfacePadding} ${surfacePadding}`,
                }}
              >
              <Tabs
                activeKey={activeKey}
                onChange={(k) => setActiveKey(k)}
                type="line"
                tabBarStyle={{
                  marginBottom: 16,
                  borderBottom: "1px solid #e9edf3",
                }}
                tabBarGutter={32}
                items={[
                  {
                    key: "todos",
                    label: (
                      <span className="fw-medium">
                        Todos{" "}
                        <span
                          className="badge bg-azul ms-2"
                          style={{ fontSize: "0.75rem" }}
                        >
                          {baseAll.length + manualAll.length}
                        </span>
                      </span>
                    ) as any,
                    children: (
                      <div
                        className="table-responsive mb-0"
                        style={tableBoxStyle}
                      >
                        <table className="table table-borderless mb-0 align-middle">
                          <thead>
                            <tr style={stickyHeaderRowStyle}>
                              <th style={checkboxThStyle}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  onChange={(e) => {
                                    if (e.currentTarget.checked)
                                      setSelectedIds(all.map((r) => r.id));
                                    else setSelectedIds([]);
                                  }}
                                  checked={
                                    all.length > 0 &&
                                    selectedIds.length === all.length
                                  }
                                />
                              </th>
                              <th
                                style={sortableThStyle("colaborador")}
                                onClick={() => setSort("colaborador")}
                              >
                                Colaborador
                              </th>
                              <th
                                style={sortableThStyle("descripcion")}
                                onClick={() => setSort("descripcion")}
                              >
                                Descripción
                              </th>
                              <th
                                style={sortableThStyle("nombre")}
                                onClick={() => setSort("nombre")}
                              >
                                Nombre
                              </th>
                              <th
                                style={sortableThStyle("monto")}
                                onClick={() => setSort("monto")}
                              >
                                Monto
                              </th>
                              <th
                                style={sortableThStyle("fechaInicio")}
                                onClick={() => setSort("fechaInicio")}
                              >
                                Fecha inicio
                              </th>
                              <th
                                style={sortableThStyle("fechaFin")}
                                onClick={() => setSort("fechaFin")}
                              >
                                Fecha fin
                              </th>
                              <th
                                style={sortableThStyle("creadoPor")}
                                onClick={() => setSort("creadoPor")}
                              >
                                Creado por
                              </th>
                              <th style={staticThStyle}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedAll.map((row, idx) => (
                              <tr
                                key={row.id || idx}
                                style={getDeductionRowStyle(row, idx)}
                                onClick={() => toggleSelected(row.id)}
                              >
                                <td onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedIds.includes(row.id)}
                                    onChange={() => toggleSelected(row.id)}
                                  />
                                </td>
                                <td className="py-3 px-3">
                                  {renderPartnerCell(row)}
                                </td>
                                <td className="py-3 px-3">
                                  {renderDescriptionCell(row)}
                                </td>
                                <td
                                  className="py-3 px-3 fw-medium"
                                  style={{
                                    color: "#101828",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.name && row.name !== "-"
                                    ? formatDisplayName(String(row.name))
                                    : "-"}
                                </td>
                                <td className="py-3 px-3">
                                  {renderAmountCell(row.amount)}
                                </td>
                                <td
                                  className="py-3 px-3"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {formatDateLong(getStartDate(row))}
                                </td>
                                <td
                                  className="py-3 px-3"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {renderEndDateCell(row)}
                                </td>
                                <td
                                  className="py-3 px-3"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {row.createdBy
                                    ? formatDisplayName(
                                        `${row.createdBy.name || ""} ${
                                          row.createdBy.lastName || ""
                                        }`.trim()
                                      ) || "-"
                                    : "-"}
                                </td>
                                <td
                                  className="py-3 px-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {renderRowActions(row)}
                                </td>
                              </tr>
                            ))}
                            {all.length === 0 && (
                              <tr>
                                <td
                                  colSpan={9}
                                  className="text-center text-muted py-5"
                                >
                                  {renderEmptyState(
                                    "No hay deducciones en este rango",
                                    "Cambia el periodo o busca un colaborador para agregarlo a la lista y capturar una deducción."
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ),
                  },
                  {
                    key: "seleccionados",
                    label: (
                      <span className="fw-medium">
                        Seleccionados{" "}
                        <span
                          className="badge bg-azul ms-2"
                          style={{ fontSize: "0.75rem" }}
                        >
                          {selectedRows.length}
                        </span>
                      </span>
                    ) as any,
                    children: (
                      <div
                        className="table-responsive mb-0"
                        style={tableBoxStyle}
                      >
                        <table className="table table-borderless mb-0 align-middle">
                          <thead>
                            <tr style={stickyHeaderRowStyle}>
                              <th style={checkboxThStyle} />
                              <th
                                style={sortableThStyle("colaborador")}
                                onClick={() => setSort("colaborador")}
                              >
                                Colaborador
                              </th>
                              <th
                                style={sortableThStyle("descripcion")}
                                onClick={() => setSort("descripcion")}
                              >
                                Descripción
                              </th>
                              <th
                                style={sortableThStyle("nombre")}
                                onClick={() => setSort("nombre")}
                              >
                                Nombre
                              </th>
                              <th
                                style={sortableThStyle("monto")}
                                onClick={() => setSort("monto")}
                              >
                                Monto
                              </th>
                              <th
                                style={sortableThStyle("fechaInicio")}
                                onClick={() => setSort("fechaInicio")}
                              >
                                Fecha inicio
                              </th>
                              <th
                                style={sortableThStyle("fechaFin")}
                                onClick={() => setSort("fechaFin")}
                              >
                                Fecha fin
                              </th>
                              <th
                                style={sortableThStyle("creadoPor")}
                                onClick={() => setSort("creadoPor")}
                              >
                                Creado por
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedRows.map((row, idx) => (
                              <tr
                                key={row.id || idx}
                                style={getDeductionRowStyle(row, idx)}
                                onClick={() => toggleSelected(row.id)}
                              >
                                <td onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedIds.includes(row.id)}
                                    onChange={() => toggleSelected(row.id)}
                                  />
                                </td>
                                <td className="py-3 px-3">
                                  {renderPartnerCell(row)}
                                </td>
                                <td className="py-3 px-3">
                                  {renderDescriptionCell(row)}
                                </td>
                                <td
                                  className="py-3 px-3 fw-medium"
                                  style={{
                                    color: "#101828",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.name && row.name !== "-"
                                    ? formatDisplayName(String(row.name))
                                    : "-"}
                                </td>
                                <td className="py-3 px-3">
                                  {renderAmountCell(row.amount)}
                                </td>
                                <td
                                  className="py-3 px-3"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {formatDateLong(getStartDate(row))}
                                </td>
                                <td
                                  className="py-3 px-3"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {renderEndDateCell(row)}
                                </td>
                                <td
                                  className="py-3 px-3"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {row.createdBy
                                    ? formatDisplayName(
                                        `${row.createdBy.name || ""} ${
                                          row.createdBy.lastName || ""
                                        }`.trim()
                                      ) || "-"
                                    : "-"}
                                </td>
                              </tr>
                            ))}
                            {selectedRows.length === 0 && (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="text-center text-muted py-5"
                                >
                                  {renderEmptyState(
                                    "Aún no hay deducciones seleccionadas",
                                    "Marca uno o más colaboradores desde la pestaña Todos para crear deducciones en lote."
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </PanelBody>
        </Panel>
      </div>
      </div>
      </div>
      </div>

      <ModalContainer
        open={modal}
        closeFn={handleCloseModal}
        title="Agregar deducciones"
        size="lg"
      >
        {selectedRows.length > 0 && (
          <div className="alert alert-warning mb-3" role="alert">
            <i className="fa fa-exclamation-triangle me-2"></i>
            <strong>
              Se han seleccionado {selectedRows.length} colaborador
              {selectedRows.length !== 1 ? "es" : ""}
            </strong>{" "}
            para registrar su deducción. Complete los siguientes campos para
            crear las deducciones para todos los colaboradores seleccionados.
          </div>
        )}
        <NewDeduction
          close={handleCloseModal}
          onRefresh={handleCreateSuccess}
        />
      </ModalContainer>

      <ModalContainer
        open={editModal}
        closeFn={handleCloseEditModal}
        title="Editar deducción"
        size="lg"
      >
        <NewDeduction
          close={handleCloseEditModal}
          onRefresh={handleCreateSuccess}
          deductionToEdit={selectedDeduction}
          isEditing={true}
        />
      </ModalContainer>

      <ModalContainer
        open={deleteModal}
        closeFn={handleCloseDeleteModal}
        title="Eliminar deducción"
        size="sm"
      >
        <DeleteDeductionModal
          closeFn={handleCloseDeleteModal}
          deduction={selectedDeduction}
          onSuccess={handleDeleteSuccess}
        />
      </ModalContainer>
    </Layout>
  );
};

export default DeduccionesPage;
